// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Per-metric trend-signal "specific" helpers (Meaning / Why / Contexts /
// Checks), pulled out of app.js. Each cluster — sensor, turbo, divergence,
// combustion — has many small helpers that cross-call within its cluster,
// so this module uses a factory:
//
//   const helpers = createTrendSignalSpecifics({
//     trendAffectedRows,
//     trendCylinderLabel,
//     trendCylinderListLabel,
//   });
//
// app.js destructures the returned helpers once, then assigns the same names
// the trend-signal definitions object expects.

import { averageValues } from "../core/math.js";
import { rawMetricDescriptor } from "../core/raw-metadata.js";

export function createTrendSignalSpecifics({
  trendAffectedRows,
  trendCylinderLabel,
  trendCylinderListLabel,
}) {
  // ---- Shared formatting helpers (used across clusters) ----

  function trendFormatElapsed(value) {
    if (!Number.isFinite(value)) return "";
    const seconds = Math.max(0, Math.round(value));
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainder = seconds % 60;
    return hours
      ? `${hours}:${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`
      : `${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`;
  }

  function trendSignedF(value) {
    if (!Number.isFinite(value)) return "";
    return `${value > 0 ? "+" : ""}${Math.round(value)} F`;
  }

  // ---- SENSOR cluster ----

  function trendSensorEvidenceNote(row) {
    const parts = [];
    if (row.sensorImpossibleJumpCount) parts.push(`${row.sensorImpossibleJumpCount.toLocaleString()} abrupt jump${row.sensorImpossibleJumpCount === 1 ? "" : "s"}`);
    if (row.sensorZeroCount) parts.push(`${row.sensorZeroCount.toLocaleString()} zero/dropout sample${row.sensorZeroCount === 1 ? "" : "s"}`);
    if (row.sensorOutOfRangeCount) parts.push(`${row.sensorOutOfRangeCount.toLocaleString()} out-of-range sample${row.sensorOutOfRangeCount === 1 ? "" : "s"}`);
    if (row.sensorPeerMismatchCount) parts.push(`${row.sensorPeerMismatchCount.toLocaleString()} peer-mismatch sample${row.sensorPeerMismatchCount === 1 ? "" : "s"}`);
    return parts.slice(0, 2).join(" · ");
  }

  function trendSensorIssueEvidenceNote(issue) {
    return trendSensorEvidenceNote({
      sensorImpossibleJumpCount: issue.impossibleJumpCount,
      sensorZeroCount: issue.zeroCount,
      sensorOutOfRangeCount: issue.outOfRangeCount,
      sensorPeerMismatchCount: issue.peerMismatchCount,
    }) || issue.evidence || "";
  }

  function trendSensorDominantIssue(rows) {
    const totals = rows.reduce((acc, row) => {
      acc.jumps += row.sensorImpossibleJumpCount ?? 0;
      acc.dropouts += row.sensorZeroCount ?? 0;
      acc.range += row.sensorOutOfRangeCount ?? 0;
      acc.peer += row.sensorPeerMismatchCount ?? 0;
      return acc;
    }, { jumps: 0, dropouts: 0, range: 0, peer: 0 });
    const ranked = [
      ["abrupt jump/noisy probe behavior", totals.jumps],
      ["zero or dropout samples", totals.dropouts],
      ["out-of-range samples", totals.range],
      ["peer-probe disagreement", totals.peer],
    ].filter(([, value]) => value > 0)
      .sort((a, b) => b[1] - a[1]);
    return ranked[0]?.[0] ?? "repeated probe-quality evidence";
  }

  function trendSensorIssueEntries(signal) {
    const sourceRows = (signal.recentFlaggedRows?.length ? signal.recentFlaggedRows : signal.flaggedRows ?? [])
      .filter((row) => Number.isFinite(row.sensorScore) && row.sensorScore < 88);
    const entries = [];
    sourceRows.forEach((row) => {
      const issues = Array.isArray(row.sensorIssues) && row.sensorIssues.length
        ? row.sensorIssues
        : [{
          key: row.sensorKey,
          label: row.sensorLabel,
          suspicion: row.sensorSuspicion,
          zeroCount: row.sensorZeroCount,
          outOfRangeCount: row.sensorOutOfRangeCount,
          impossibleJumpCount: row.sensorImpossibleJumpCount,
          peerMismatchCount: row.sensorPeerMismatchCount,
        }];
      issues.forEach((issue) => {
        if (!issue?.label && !issue?.key) return;
        entries.push({ row, issue });
      });
    });
    return entries;
  }

  function trendSensorIssueGroups(signal) {
    const groups = new Map();
    trendSensorIssueEntries(signal).forEach(({ row, issue }) => {
      const label = issue.label || issue.key || "Probe channel";
      if (!groups.has(label)) {
        groups.set(label, {
          label,
          key: issue.key ?? null,
          rows: [],
          issueRows: [],
          jumps: 0,
          dropouts: 0,
          range: 0,
          peer: 0,
          suspicion: 0,
        });
      }
      const group = groups.get(label);
      group.rows.push(row);
      group.issueRows.push({ row, issue });
      group.jumps += issue.impossibleJumpCount ?? 0;
      group.dropouts += issue.zeroCount ?? 0;
      group.range += issue.outOfRangeCount ?? 0;
      group.peer += issue.peerMismatchCount ?? 0;
      group.suspicion += issue.suspicion ?? 0;
    });
    return [...groups.values()]
      .map((group) => ({
        ...group,
        flightCount: new Set(group.rows.map((row) => row.id)).size,
        sortWeight: group.suspicion + group.jumps * 4 + group.dropouts * 3 + group.range * 5 + group.peer,
      }))
      .sort((a, b) => b.flightCount - a.flightCount || b.sortWeight - a.sortWeight || a.label.localeCompare(b.label));
  }

  function trendSensorGroupDominantIssue(group) {
    return trendSensorDominantIssue([{
      sensorImpossibleJumpCount: group.jumps,
      sensorZeroCount: group.dropouts,
      sensorOutOfRangeCount: group.range,
      sensorPeerMismatchCount: group.peer,
    }]);
  }

  function trendSensorGroupDisplayLabel(group) {
    const raw = group.label || group.key || "Probe channel";
    if (raw.includes(" ")) return raw;
    return rawMetricDescriptor(group.key || raw)?.[0] || raw;
  }

  function trendSensorGroupSummary(group) {
    return `${trendSensorGroupDisplayLabel(group)} — ${trendSensorGroupDominantIssue(group)} across ${group.flightCount} flight${group.flightCount === 1 ? "" : "s"}`;
  }

  function trendSensorSpecificMeaning(signal) {
    const groups = trendSensorIssueGroups(signal);
    if (!groups.length) return null;
    const primary = groups[0];
    const others = groups.slice(1, 5);
    const notes = primary.issueRows
      .map(({ issue }) => trendSensorIssueEvidenceNote(issue))
      .filter(Boolean);
    const evidence = notes.length
      ? ` Examples include ${[...new Set(notes)].slice(0, 2).join("; ")}.`
      : "";
    const otherText = others.length
      ? ` Other affected probe channels also show up: ${others.map(trendSensorGroupSummary).join("; ")}.`
      : "";
    return `${trendSensorGroupDisplayLabel(primary)} is the probe channel flagged most often for data-quality issues in this aircraft's history, mainly from ${trendSensorGroupDominantIssue(primary)}.${evidence}${otherText}`;
  }

  function trendSensorSpecificWhy(signal) {
    const groups = trendSensorIssueGroups(signal);
    if (!groups.length) return null;
    const multi = groups.length > 1
      ? `${groups.length} probe channels repeat in the flagged history`
      : `the same channel repeats across ${groups[0].flightCount} flagged flight${groups[0].flightCount === 1 ? "" : "s"}`;
    return `Because ${multi}, this looks more like probe, wiring, connector, or monitor-channel trouble than a one-off engine event. Treat dependent CHT, EGT balance, pairing, valve, cooling, and divergence findings as lower confidence until each affected trace is checked.`;
  }

  function trendSensorSpecificContexts(signal) {
    const groups = trendSensorIssueGroups(signal);
    if (!groups.length) return [];
    return [
      ...groups.slice(0, 6).map(trendSensorGroupSummary),
      ...groups[0].issueRows
        .map(({ row, issue }) => {
          const note = trendSensorIssueEvidenceNote(issue);
          return note ? `${row.dateLabel || row.title} · ${trendSensorGroupDisplayLabel(groups[0])} — ${note}` : "";
        })
        .filter(Boolean)
        .slice(0, 2),
    ];
  }

  // ---- TURBO cluster ----

  function trendTurboValueLabel(key, value) {
    if (!Number.isFinite(value)) return "";
    if (key === "map" || key === "upperDeckPressure") return `${value.toFixed(1)} ${key === "map" ? "inHg" : "psi"}`;
    return `${Math.round(value)} F`;
  }

  function trendTurboEventSummary(event) {
    if (!event?.label) return "";
    const peak = trendTurboValueLabel(event.key, event.peakValue);
    const limit = trendTurboValueLabel(event.key, event.limit);
    if (peak && limit) return `${event.label}: peak ${peak} versus limit ${limit}`;
    return event.label;
  }

  function trendTurboStatusGroups(signal) {
    const rows = trendAffectedRows(signal);
    const groups = new Map();
    rows.forEach((row) => {
      const status = row.turboStatus ?? "review";
      if (!groups.has(status)) groups.set(status, []);
      groups.get(status).push(row);
    });
    return [...groups.entries()]
      .map(([status, groupRows]) => ({ status, rows: groupRows }))
      .sort((a, b) => b.rows.length - a.rows.length || a.status.localeCompare(b.status));
  }

  function trendTurboPrimaryReason(signal) {
    const groups = trendTurboStatusGroups(signal);
    const primary = groups[0];
    if (!primary) return "turbo/boost review context";
    if (primary.status === "needs-profile-limits") return "the aircraft profile is missing turbo limits, so the app cannot judge MAP/TIT against POH/AFM values";
    if (primary.status === "data-quality-review") return "TIT signal quality is affecting the turbo review";
    if (primary.status === "limit-review") return "one or more configured turbo limits were exceeded";
    if (primary.status === "heat-margin-review") return "high-power heat and fuel-flow context deserves review";
    if (primary.status === "heat-margin-watch") return "high-power heat context is worth watching";
    if (primary.status === "margin-watch") return "TIT is operating near the configured margin";
    return "turbo data is present and being trended for context";
  }

  function trendTurboPeakSummary(rows) {
    const maxTit = Math.max(...rows.map((row) => row.maxTit).filter(Number.isFinite));
    const maxMap = Math.max(...rows.map((row) => row.maxMap).filter(Number.isFinite));
    const maxDeck = Math.max(...rows.map((row) => row.peakUpperDeck).filter(Number.isFinite));
    const parts = [];
    if (Number.isFinite(maxTit)) parts.push(`highest TIT ${Math.round(maxTit)} F`);
    if (Number.isFinite(maxMap)) parts.push(`highest MAP ${maxMap.toFixed(1)} inHg`);
    if (Number.isFinite(maxDeck)) parts.push(`highest upper-deck pressure ${maxDeck.toFixed(1)} psi`);
    return parts.join(", ");
  }

  function trendTurboSpecificMeaning(signal) {
    const rows = trendAffectedRows(signal);
    if (!rows.length) return null;
    const reason = trendTurboPrimaryReason(signal);
    const peaks = trendTurboPeakSummary(rows);
    const limitEvents = rows.flatMap((row) => row.turboLimitEvents ?? []).map(trendTurboEventSummary).filter(Boolean);
    const eventText = limitEvents.length ? ` Flagged examples: ${[...new Set(limitEvents)].slice(0, 3).join("; ")}.` : "";
    const peakText = peaks ? ` Across the flagged flights, the recorded turbo context includes ${peaks}.` : "";
    return `The repeated Turbo/Boost item is specifically that ${reason}.${peakText}${eventText}`;
  }

  function trendTurboSpecificWhy(signal) {
    const rows = trendAffectedRows(signal);
    if (!rows.length) return null;
    const hasMissingLimits = rows.some((row) => row.turboStatus === "needs-profile-limits");
    const hasQuality = rows.some((row) => row.turboStatus === "data-quality-review" || row.turboTitQualityPenalty > 0);
    if (hasMissingLimits) return "Without max MAP, max TIT, and any installed induction or compressor-temperature limits in the aircraft profile, the app can show the recorded values but cannot call an overboost or TIT exceedance.";
    if (hasQuality) return "A noisy or suspect TIT channel can make a turbo trend look more serious than it is, so TIT-based conclusions need raw-trace review before treating them as boost-control or temperature-margin evidence.";
    if (rows.some((row) => row.turboStatus === "heat-margin-review" || row.turboStatus === "heat-margin-watch")) return "Repeated high-power heat cues matter most when MAP, RPM, fuel-flow expectations, TIT, CHT, OAT, altitude, and climb profile line up across comparable flights.";
    return "Repeated MAP, TIT, or boost-pressure review items matter most when compared against configured limits and similar high-power or altitude phases.";
  }

  function trendTurboSpecificContexts(signal) {
    const rows = trendAffectedRows(signal);
    const items = [];
    const groups = trendTurboStatusGroups(signal);
    groups.forEach((group) => {
      const label = {
        "needs-profile-limits": "Missing profile limits",
        "data-quality-review": "TIT signal quality",
        "limit-review": "Configured limit review",
        "margin-watch": "TIT margin watch",
      }[group.status] ?? "Turbo context";
      items.push(`${label}: ${group.rows.length} flagged flight${group.rows.length === 1 ? "" : "s"}`);
    });
    const qualityRow = rows.find((row) => row.turboTitQualityPenalty > 0 || row.turboTitQualityScore < 88);
    if (qualityRow?.turboTitQualityLabel) {
      const detail = qualityRow.turboTitQualityJumps
        ? `${qualityRow.turboTitQualityJumps} abrupt jump${qualityRow.turboTitQualityJumps === 1 ? "" : "s"}`
        : qualityRow.turboTitQualityUnstableSamples
          ? `${qualityRow.turboTitQualityUnstableSamples} unstable samples`
          : "";
      items.push(`${qualityRow.turboTitQualityLabel} quality${detail ? `: ${detail}` : ""}`);
    }
    const peaks = trendTurboPeakSummary(rows);
    if (peaks) items.push(`Flagged-flight peaks: ${peaks}`);
    return items;
  }

  // ---- DIVERGENCE cluster ----

  function trendDivergencePrimaryRows(signal) {
    return trendAffectedRows(signal)
      .filter((row) => Number.isFinite(row.divergenceEgtLift) || Number.isFinite(row.divergenceChtLift));
  }

  function trendDivergenceRepeatedCylinders(rows) {
    const counts = new Map();
    rows.forEach((row) => {
      (row.divergenceCylinders ?? []).forEach((cylinder) => {
        if (!Number.isFinite(cylinder)) return;
        counts.set(cylinder, (counts.get(cylinder) ?? 0) + 1);
      });
    });
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1] || a[0] - b[0])
      .map(([cylinder, count]) => ({ cylinder, count }));
  }

  function trendDivergenceLiftSummary(rows) {
    const egt = rows.map((row) => row.divergenceEgtLift).filter(Number.isFinite);
    const cht = rows.map((row) => row.divergenceChtLift).filter(Number.isFinite);
    const maxEgt = egt.length ? Math.max(...egt) : null;
    const maxCht = cht.length ? Math.max(...cht) : null;
    const avgEgt = averageValues(egt);
    const avgCht = averageValues(cht);
    const parts = [];
    if (Number.isFinite(avgEgt) && Number.isFinite(maxEgt)) parts.push(`EGT spread averaged +${Math.round(avgEgt)} F, max +${Math.round(maxEgt)} F`);
    if (Number.isFinite(avgCht) && Number.isFinite(maxCht)) parts.push(`CHT spread averaged +${Math.round(avgCht)} F, max +${Math.round(maxCht)} F`);
    return parts.join("; ");
  }

  function trendDivergenceWindowSummary(row) {
    const start = trendFormatElapsed(row.divergenceStartTime);
    const end = trendFormatElapsed(row.divergenceEndTime);
    const window = start && end ? `${start}-${end}` : "window unavailable";
    const egt = Number.isFinite(row.divergenceEgtLift) ? `EGT +${Math.round(row.divergenceEgtLift)} F` : "";
    const cht = Number.isFinite(row.divergenceChtLift) ? `CHT +${Math.round(row.divergenceChtLift)} F` : "";
    const cylinders = trendCylinderListLabel(row.divergenceCylinders, row);
    return `${row.dateLabel}: ${window}${cylinders ? `, ${cylinders}` : ""}${egt || cht ? `, ${[egt, cht].filter(Boolean).join(", ")}` : ""}`;
  }

  function trendDivergenceSpecificMeaning(signal) {
    const rows = trendDivergencePrimaryRows(signal);
    if (!rows.length) return null;
    const repeated = trendDivergenceRepeatedCylinders(rows);
    const primary = repeated[0];
    const lift = trendDivergenceLiftSummary(rows);
    const stableCount = rows.filter((row) => row.divergencePowerStable === true).length;
    const powerText = stableCount
      ? ` In ${stableCount} flagged flight${stableCount === 1 ? "" : "s"}, logged power context looked stable during the split.`
      : " Logged power or flight-condition movement may explain part of the split, so compare the exact windows.";
    const cylinderText = primary
      ? `${trendCylinderLabel(primary.cylinder, rows)} appears in ${primary.count} of the flagged flight${primary.count === 1 ? "" : "s"}`
      : "the flagged cylinders vary by flight";
    return `The repeated transient-divergence item is specifically that ${cylinderText}; the traces fan apart for a short window and then partly reconverge.${lift ? ` ${lift}.` : ""}${powerText}`;
  }

  function trendDivergenceSpecificWhy(signal) {
    const rows = trendDivergencePrimaryRows(signal);
    if (!rows.length) return null;
    const repeated = trendDivergenceRepeatedCylinders(rows);
    const sameCylinder = repeated[0]?.count >= Math.max(2, Math.ceil(rows.length * 0.5));
    const stableCount = rows.filter((row) => row.divergencePowerStable === true).length;
    if (sameCylinder && stableCount) {
      return `The same cylinder shows up more than once, and in some of those moments the power setting looked steady. That makes it more worth a closer look than a single odd blip. Still, check the raw graph and sensor quality before assuming it is an engine problem.`;
    }
    if (sameCylinder) {
      return `The same cylinder shows up more than once, so compare the marked spots across flights. If power, mixture, and airspeed were similar each time, the repeat matters more than one odd moment by itself.`;
    }
    return "This matters most when the same split happens again under similar conditions. If the marked spots line up with power, mixture, descent, or airspeed changes, it may simply reflect how the airplane was being operated.";
  }

  function trendDivergenceSpecificContexts(signal) {
    const rows = trendDivergencePrimaryRows(signal);
    if (!rows.length) return [];
    const repeated = trendDivergenceRepeatedCylinders(rows);
    const contexts = [];
    if (repeated.length) {
      contexts.push(`Most repeated: ${repeated.slice(0, 3).map((item) => `${trendCylinderLabel(item.cylinder, rows)} in ${item.count}`).join("; ")}`);
    }
    const lift = trendDivergenceLiftSummary(rows);
    if (lift) contexts.push(lift);
    const stableCount = rows.filter((row) => row.divergencePowerStable === true).length;
    const movingCount = rows.filter((row) => row.divergencePowerStable === false).length;
    if (stableCount || movingCount) contexts.push(`Power context: ${stableCount} stable window${stableCount === 1 ? "" : "s"}, ${movingCount} moving/transition window${movingCount === 1 ? "" : "s"}`);
    rows.slice(-3).forEach((row) => contexts.push(trendDivergenceWindowSummary(row)));
    return contexts;
  }

  // ---- COMBUSTION cluster ----

  function trendCombustionPrimaryRows(signal) {
    return trendAffectedRows(signal)
      .filter((row) => Number.isFinite(row.combustionScore) && row.combustionScore < 88);
  }

  function trendCombustionSignatureLabel(type) {
    return {
      coldCylinder: "relative cold-cylinder pattern",
      ignitionCylinder: "single-cylinder split: EGT rose while CHT fell",
      globalIgnition: "all-cylinder split: EGT rose while CHT fell",
      globalMixture: "fuel-flow-related all-cylinder split",
      injectorRop: "same-cylinder rise: EGT and CHT rose together",
      injectorLop: "same-cylinder drop: EGT and CHT fell together",
    }[type] ?? "paired CHT/EGT movement";
  }

  function trendCombustionSignatureGroups(rows) {
    const groups = new Map();
    rows.forEach((row) => {
      const type = row.combustionSignature ?? "unknown";
      const cylinder = Number.isFinite(row.combustionCylinder) ? row.combustionCylinder : null;
      const key = `${type}:${cylinder ?? "all"}`;
      if (!groups.has(key)) groups.set(key, { type, cylinder, rows: [] });
      groups.get(key).rows.push(row);
    });
    return [...groups.values()]
      .sort((a, b) => b.rows.length - a.rows.length
        || (a.cylinder ?? 999) - (b.cylinder ?? 999)
        || a.type.localeCompare(b.type));
  }

  function trendCombustionMovementSummary(row) {
    const type = row.combustionSignature;
    const isGlobal = type === "globalIgnition" || type === "globalMixture";
    const egt = trendSignedF(isGlobal ? row.combustionCommonEgtChange : row.combustionEgtChange);
    const cht = trendSignedF(isGlobal ? row.combustionCommonChtChange : row.combustionChtChange);
    if (egt && cht) {
      const scope = isGlobal ? "median engine movement" : "movement versus peer cylinders";
      return `EGT ${egt} and CHT ${cht} (${scope})`;
    }
    return row.combustionMeta ?? "";
  }

  function trendCombustionWindowSummary(row) {
    const start = trendFormatElapsed(row.combustionStartTime);
    const end = trendFormatElapsed(row.combustionEndTime);
    const window = start && end ? `${start}-${end}` : "window unavailable";
    const cylinder = trendCylinderLabel(row.combustionCylinder, row);
    const movement = trendCombustionMovementSummary(row);
    const context = row.combustionFuelFlowThermalDetail || row.combustionPowerMovementDetail || "";
    return `${row.dateLabel}: ${window}${cylinder ? `, ${cylinder}` : ""}${movement ? `, ${movement}` : ""}${context ? `; ${context}` : ""}`;
  }

  function trendCombustionSpecificMeaning(signal) {
    const rows = trendCombustionPrimaryRows(signal);
    if (!rows.length) return null;
    const primary = trendCombustionSignatureGroups(rows)[0];
    const label = trendCombustionSignatureLabel(primary.type);
    const subject = Number.isFinite(primary.cylinder) ? trendCylinderLabel(primary.cylinder, rows) : "the whole engine";
    const example = primary.rows
      .slice()
      .sort((a, b) => (a.combustionScore ?? 100) - (b.combustionScore ?? 100))[0];
    const movement = example ? trendCombustionMovementSummary(example) : "";
    const exampleText = example && movement
      ? ` Clearest example: ${trendCombustionWindowSummary(example)}.`
      : "";
    return `The repeated CHT/EGT pairing item is specifically that ${subject} matched the "${label}" pattern in ${primary.rows.length} flagged flight${primary.rows.length === 1 ? "" : "s"}. That is what caused the flag: during stable-power checks, the app saw CHT and EGT move in this paired shape instead of tracking like the peer cylinders.${exampleText}`;
  }

  function trendCombustionSpecificWhy(signal) {
    const rows = trendCombustionPrimaryRows(signal);
    if (!rows.length) return null;
    const primary = trendCombustionSignatureGroups(rows)[0];
    const sameCylinder = Number.isFinite(primary.cylinder) && primary.rows.length >= Math.max(2, Math.ceil(rows.length * 0.5));
    const hasFuelContext = rows.some((row) => row.combustionSignature === "globalMixture" || row.combustionFuelFlowThermalDetail);
    if (hasFuelContext) {
      return "Fuel flow or power was changing during at least part of this pattern. First check whether normal leaning, enrichment, or a power change explains the shape before looking for a mechanical problem.";
    }
    if (sameCylinder) {
      return `${trendCylinderLabel(primary.cylinder, rows)} shows up more than once, so this is more useful than a single odd-looking graph. Before blaming a plug, lead, injector, air leak, cooling issue, or cylinder problem, confirm it on the raw graph and make sure the sensors and fuel-flow context look trustworthy.`;
    }
    return "This matters only when the same CHT and EGT shape happens again in similar flying conditions. If it moves between cylinders or phases of flight, the clue is weaker.";
  }

  function trendCombustionSpecificContexts(signal) {
    const rows = trendCombustionPrimaryRows(signal);
    if (!rows.length) return [];
    const contexts = trendCombustionSignatureGroups(rows).slice(0, 4).map((group) => {
      const subject = Number.isFinite(group.cylinder) ? trendCylinderLabel(group.cylinder, rows) : "whole engine";
      return `${subject}: ${trendCombustionSignatureLabel(group.type)} in ${group.rows.length} flagged flight${group.rows.length === 1 ? "" : "s"}`;
    });
    rows
      .slice()
      .sort((a, b) => (a.combustionScore ?? 100) - (b.combustionScore ?? 100))
      .slice(0, 2)
      .forEach((row) => contexts.push(trendCombustionWindowSummary(row)));
    return contexts;
  }

  function trendCombustionSpecificChecks(signal) {
    const rows = trendCombustionPrimaryRows(signal);
    if (!rows.length) return [];
    const primary = trendCombustionSignatureGroups(rows)[0];
    const subject = Number.isFinite(primary.cylinder) ? trendCylinderLabel(primary.cylinder, rows) : "the affected traces";
    return [
      `Open the clearest flagged window and verify the actual paired movement on ${subject}.`,
      "Keep fuel flow, RPM, MAP, and phase visible so normal leaning or power movement is not mistaken for a cylinder issue.",
      "Compare the same cylinder and same phase in the other flagged flights to see whether the shape truly repeats.",
    ];
  }

  return {
    // Shared
    trendFormatElapsed,
    trendSignedF,
    // Sensor
    trendSensorEvidenceNote,
    trendSensorIssueEvidenceNote,
    trendSensorDominantIssue,
    trendSensorIssueEntries,
    trendSensorIssueGroups,
    trendSensorGroupDominantIssue,
    trendSensorGroupDisplayLabel,
    trendSensorGroupSummary,
    trendSensorSpecificMeaning,
    trendSensorSpecificWhy,
    trendSensorSpecificContexts,
    // Turbo
    trendTurboValueLabel,
    trendTurboEventSummary,
    trendTurboStatusGroups,
    trendTurboPrimaryReason,
    trendTurboPeakSummary,
    trendTurboSpecificMeaning,
    trendTurboSpecificWhy,
    trendTurboSpecificContexts,
    // Divergence
    trendDivergencePrimaryRows,
    trendDivergenceRepeatedCylinders,
    trendDivergenceLiftSummary,
    trendDivergenceWindowSummary,
    trendDivergenceSpecificMeaning,
    trendDivergenceSpecificWhy,
    trendDivergenceSpecificContexts,
    // Combustion
    trendCombustionPrimaryRows,
    trendCombustionSignatureLabel,
    trendCombustionSignatureGroups,
    trendCombustionMovementSummary,
    trendCombustionWindowSummary,
    trendCombustionSpecificMeaning,
    trendCombustionSpecificWhy,
    trendCombustionSpecificContexts,
    trendCombustionSpecificChecks,
  };
}
